import { ReactNode, useState } from 'react';

import { useDrawdownRepaymentSchedule } from '@api/lendingAPI/lapiHooks';
import { downloadFile } from '@api/lendingAPI/LendingAPI';
import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { tracking } from '@iwoca/frontend-tracking-library';
import {
    IconAngleLeft,
    IconDownload,
    IconInfoCircleFilled
} from '@iwoca/icons';
import { Button, Input, Label, Text } from '@iwoca/orion';
import { useMutation } from '@tanstack/react-query';
import { Skeleton } from '@ui';
import { addDays, format } from 'date-fns';
import { FormattedMessage } from 'react-intl';

import {
    formatDurationFromDate,
    getOfferDurationPreset,
    PRESET_DATES
} from './RepaymentCalculator.helpers';
import { useStateKey } from '../../../../../../../iwQueryClient/useStateKey';
import { buildRepaymentScheduleState } from '../../../../../../modals/repaymentScheduleModal/RepaymentScheduleHelper';
import { RepaymentScheduleTable } from '../../../../../../modals/repaymentScheduleModal/RepaymentScheduleTable';
import { MoneyWithDecimals } from '../../../../../../ui/moneyWithDecimals';

const SummaryRow = ({
    label,
    value,
    isLoading
}: {
    label: ReactNode;
    value: ReactNode;
    isLoading: boolean;
}) => (
    <div className="flex justify-between py-s">
        <Text className="font-reg">{label}</Text>
        <Text className="font-bold">
            {isLoading ? (
                <Skeleton
                    data-testid="summary-row-skeleton"
                    className="h-[20px] w-[80px]"
                />
            ) : (
                value
            )}
        </Text>
    </div>
);

export const RepaymentCalculator = ({
    drawdownAmount,
    selectedOffer,
    defaultDate
}: {
    drawdownAmount: number;
    selectedOffer: TFormalOffer;
    defaultDate?: string;
}) => {
    const stateKey = useStateKey();
    const offerDurationPreset = getOfferDurationPreset(selectedOffer);
    const allPresets = [...PRESET_DATES, offerDurationPreset];
    const initialDate =
        defaultDate ?? format(PRESET_DATES[0].getDate(), 'yyyy-MM-dd');
    const initialPreset = allPresets.find(
        (preset) => format(preset.getDate(), 'yyyy-MM-dd') === initialDate
    );
    const [selectedDate, setSelectedDate] = useState<string | null>(
        initialDate
    );
    const [activePreset, setActivePreset] = useState<string | null>(
        initialPreset?.key ?? null
    );
    const [showRepaymentSchedule, setShowRepaymentSchedule] = useState(false);

    const offerId = selectedOffer.offer_id!;
    const minDate = format(new Date(), 'yyyy-MM-dd');
    const maxDate = format(
        addDays(new Date(), selectedOffer.duration!.amount),
        'yyyy-MM-dd'
    );

    const { data, isLoading, isError } = useDrawdownRepaymentSchedule({
        offerId,
        drawdownAmount,
        earlyFinalRepaymentDate: selectedDate,
        enabled: Boolean(selectedDate)
    });

    const scheduleData = data?.summary;

    const SCHEDULE_DOWNLOAD_URL = `/api/lending/edge/draw_down_repayment_schedule/${stateKey}/credit_facility/${offerId}/?amount=${drawdownAmount}${selectedDate ? `&early_final_repayment_date=${selectedDate}` : ''}`;

    const { mutate: downloadSchedule, isLoading: isDownloading } = useMutation({
        mutationKey: ['downloadRepaymentSchedule', SCHEDULE_DOWNLOAD_URL],
        mutationFn: () => downloadFile({ url: SCHEDULE_DOWNLOAD_URL })
    });

    const handleDateChange = (selectedDate: string, presetLabel?: string) => {
        if (Date.parse(selectedDate) >= Date.parse(minDate)) {
            setSelectedDate(selectedDate);
            tracking.ampli.earlyRepaymentCalculatorUsed({
                early_repayment_date: selectedDate,
                preset_button: presetLabel ?? ''
            });
        }
    };

    if (showRepaymentSchedule && !isLoading && data) {
        return (
            <div className="mt-2xl">
                <button
                    onClick={() => {
                        setShowRepaymentSchedule(false);
                        tracking.ampli.repaymentScheduleViewed();
                    }}
                    data-testid="back-to-calculator"
                    aria-label="Back"
                    className="absolute left-s top-xs z-20 flex h-[40px] w-[40px] items-center justify-center rounded-s bg-structure-100 text-structure-0 hover:bg-structure-95"
                >
                    <IconAngleLeft fontSize="24px" />
                </button>

                <div className="mb-l flex items-center justify-between">
                    <Text className="text-2xl font-bold">
                        <FormattedMessage
                            id="_.repayment_calculator.schedule_title"
                            defaultMessage="Repayment schedule"
                        />
                    </Text>
                    <Button
                        onClick={() => downloadSchedule()}
                        loading={isDownloading}
                        variant="tertiary"
                        className="h-auto p-0"
                    >
                        <FormattedMessage
                            id="_.repayment_calculator.download_button"
                            defaultMessage="Download"
                        />
                        <IconDownload />
                    </Button>
                </div>

                <RepaymentScheduleTable
                    {...buildRepaymentScheduleState({
                        schedule: data.schedule,
                        summary: data.summary
                    })}
                />
            </div>
        );
    }

    return (
        <div className="mt-xl">
            <Text className="mb-l text-2xl font-bold">
                <FormattedMessage
                    id="_.repayment_calculator_modal.title"
                    defaultMessage="Calculate early repayment savings"
                />
            </Text>

            <Text className="mb-s font-bold">
                <FormattedMessage
                    id="_.inline.early_repayment.titleLineTwo"
                    defaultMessage="Select a repayment date to see how it could look"
                />
            </Text>

            <div className="flex flex-wrap items-center justify-between gap-m">
                <Label
                    className="shrink-0 text-s font-reg"
                    htmlFor="repayment-date-picker"
                >
                    <FormattedMessage
                        id="_.choose_amount.early_repayment.repay"
                        defaultMessage="What if I repay early in full on:"
                    />
                </Label>

                <Input
                    id="repayment-date-picker"
                    className="p-s font-bold text-structure-40"
                    type="date"
                    value={selectedDate ?? ''}
                    min={minDate}
                    max={maxDate}
                    onChange={(e) => {
                        setActivePreset(null);
                        handleDateChange(e.target.value);
                    }}
                />
            </div>

            <div className="mt-m flex flex-col gap-s min-sm:flex-row min-sm:items-center min-sm:justify-between">
                <Text className="shrink-0 text-s text-structure-40">
                    <FormattedMessage
                        id="_.repayment_calculator.or_in"
                        defaultMessage="or in..."
                    />
                </Text>
                <div className="flex max-w-[464px] grow gap-s">
                    {allPresets.map((preset) => (
                        <button
                            key={preset.key}
                            className={`grow rounded-s border px-xs py-s text-s font-bold text-primary-40 min-sm:px-s min-sm:text-m ${
                                activePreset === preset.key
                                    ? 'border-primary-40'
                                    : 'border-structure-80'
                            }`}
                            onClick={() => {
                                setActivePreset(preset.key);
                                handleDateChange(
                                    format(preset.getDate(), 'yyyy-MM-dd'),
                                    preset.key
                                );
                            }}
                        >
                            {preset.label}
                        </button>
                    ))}
                </div>
            </div>

            {selectedDate && isError && (
                <Text className="pt-l text-center text-s font-med text-core-40">
                    <FormattedMessage
                        id="_.compare_offers_table.error"
                        defaultMessage="Something went wrong. Please try again later."
                    />
                </Text>
            )}

            {selectedDate && !isError && (
                <div className="mt-2xl">
                    <Text className="font-bold">
                        <FormattedMessage
                            id="_.repayment_calculator.breakdown_title"
                            defaultMessage="If you repay in full in {duration}..."
                            values={{
                                duration: formatDurationFromDate(selectedDate)
                            }}
                        />
                    </Text>

                    <div className="mt-m">
                        <SummaryRow
                            label={
                                <FormattedMessage
                                    id="_.repayment_calculator.total_interest"
                                    defaultMessage="Total interest"
                                />
                            }
                            value={
                                <MoneyWithDecimals
                                    value={scheduleData?.total_interest}
                                />
                            }
                            isLoading={isLoading}
                        />
                        <SummaryRow
                            label={
                                <FormattedMessage
                                    id="_.repayment_calculator.early_repayment_fees"
                                    defaultMessage="Early repayment fees"
                                />
                            }
                            value={
                                <FormattedMessage
                                    id="_.repayment_calculator.always_zero_fees"
                                    defaultMessage="always £0.00"
                                />
                            }
                            isLoading={false}
                        />
                        <SummaryRow
                            label={
                                <FormattedMessage
                                    id="_.repayment_calculator.withdrawal_fee"
                                    defaultMessage="Withdrawal fee ({percentage}%)"
                                    values={{
                                        percentage: parseFloat(
                                            (
                                                (selectedOffer.withdrawal_fee_percentage ??
                                                    0) * 100
                                            ).toFixed(2)
                                        )
                                    }}
                                />
                            }
                            value={
                                <MoneyWithDecimals
                                    value={scheduleData?.total_fees}
                                />
                            }
                            isLoading={isLoading}
                        />
                        <SummaryRow
                            label={
                                <FormattedMessage
                                    id="_.repayment_calculator.total_repayment"
                                    defaultMessage="Total repayment"
                                />
                            }
                            value={
                                <MoneyWithDecimals
                                    value={scheduleData?.total_payable}
                                />
                            }
                            isLoading={isLoading}
                        />
                    </div>

                    <div className="mt-l flex flex-wrap items-center justify-between gap-m">
                        <Button
                            variant="tertiary"
                            onClick={() => setShowRepaymentSchedule(true)}
                            className="h-auto p-0"
                        >
                            <FormattedMessage
                                id="_.repayment_calculator.see_schedule"
                                defaultMessage="View {duration} repayment schedule"
                                values={{
                                    duration:
                                        formatDurationFromDate(selectedDate)
                                }}
                            />
                        </Button>
                        {(isLoading ||
                            (scheduleData?.amount_saved ?? 0) > 0) && (
                            <div className="grow text-right font-reg text-success-drk">
                                <FormattedMessage
                                    id="_.repayment_calculator.savings"
                                    defaultMessage="this saves you {amount}"
                                    values={{
                                        amount: isLoading ? (
                                            <Skeleton className="h-[20px] w-[80px]" />
                                        ) : (
                                            <span className="font-bold">
                                                <MoneyWithDecimals
                                                    value={
                                                        scheduleData?.amount_saved ??
                                                        0
                                                    }
                                                />
                                            </span>
                                        )
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    <div className="mt-l flex items-start gap-m rounded-m bg-primary-97 p-m">
                        <IconInfoCircleFilled
                            className="shrink-0 text-secondary-40"
                            fontSize="20px"
                        />
                        <Text className="text-m font-med text-secondary-40">
                            <FormattedMessage
                                id="_.choose_amount.early_repayment.info"
                                defaultMessage="Your repayment schedule will always be for {months} months but you can repay early in your dashboard at any time."
                                values={{
                                    months: getDurationInMonths(
                                        selectedOffer.duration!
                                    )
                                }}
                            />
                        </Text>
                    </div>
                </div>
            )}
        </div>
    );
};
